import type { CategoryDeleteBlockedData } from '@avisoma/shared';

const BLOCKED_REASONS: ReadonlyArray<CategoryDeleteBlockedData['reason']> = [
  'pages_linked',
  'documents_linked',
  'protected_name',
  'supertype_referenced',
];

export type CategoryGuardFailure = {
  /** The server's own explanation — already reason-specific and user-readable. */
  message: string;
  /** The structured 409 payload when the server refused on a guard, else null. */
  blocked: CategoryDeleteBlockedData | null;
};

/**
 * Read a failed category delete/rename response.
 *
 * The category routes refuse unsafe writes with a 409 carrying a machine-readable
 * `reason` (see `CategoryDeleteBlockedData`), so the UI can say WHICH guard fired
 * instead of showing a bare "request failed". Shared by the delete dialog and the
 * rename field so both parse the envelope one way.
 */
export function readCategoryGuardError(error: unknown): CategoryGuardFailure {
  // The API answers { error: { message, data } } — read the structured halves, not the
  // bare axios object (same shape the other pages destructure).
  const response = (
    error as {
      response?: { status?: number; data?: { error?: { message?: string; data?: CategoryDeleteBlockedData } } };
    }
  ).response;

  // Only a 409 carries a guard payload. Any other status with a `data` field is some
  // unrelated error's own payload, and reading a `reason` off it would show the user a
  // confident, wrong explanation.
  const payload = response?.status === 409 ? (response.data?.error?.data ?? null) : null;
  const blocked = payload !== null && BLOCKED_REASONS.includes(payload.reason) ? payload : null;

  return {
    message: response?.data?.error?.message ?? (error as Error)?.message ?? 'Request failed',
    blocked,
  };
}
